import { useState, useRef, useEffect, useCallback } from 'react';
import { INSTRUMENTS } from '../constants';
import {
  emailExists, createMusician,
  sendLoginCode, verifyLoginCode, linkAuthUser, getMyProfile, getCurrentSession,
} from '../lib/supabase';
import { PRIVACY_POLICY, TERMS_CONDITIONS } from '../legal';
import Modal from '../ui/Modal';

const LEGACY_USER_KEY = 'jamup_user';

// ─── Messaggi di errore in italiano ──────────────────────────────────────────
function messaggioErrore(e) {
  const t = (e?.message || '').toLowerCase();
  if (t.includes('expired') || t.includes('invalid')) return 'Codice non valido o scaduto. Chiedine uno nuovo.';
  if (t.includes('rate limit') || t.includes('for security purposes')) return 'Troppi tentativi ravvicinati. Aspetta un minuto e riprova.';
  if (t.includes('signups not allowed') || t.includes('not authorized')) return 'Accesso non consentito per questa email.';
  return 'Qualcosa non ha funzionato. Controlla la connessione e riprova.';
}

// ─── City Autocomplete ────────────────────────────────────────────────────────
function CityAutocomplete({ value, onChange, inputStyle }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const [validated, setValidated] = useState(!!value);
  const timerRef = useRef(null);

  const search = async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowSug(false); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=5&countrycodes=it`,
        { headers: { 'Accept-Language': 'it' } }
      );
      const data = await res.json();
      const cities = [...new Map(
        data.map(d => d.address?.city || d.address?.town || d.address?.village || d.address?.municipality)
          .filter(Boolean).map(c => [c, c])
      ).values()];
      setSuggestions(cities);
      setShowSug(cities.length > 0);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input style={{ ...inputStyle, borderColor: validated ? '#ED4D1D' : undefined }}
          placeholder="es. Milano" value={value} autoComplete="off"
          onChange={e => {
            setValidated(false);
            onChange(e.target.value, false);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => search(e.target.value), 400);
          }}
          onFocus={() => suggestions.length > 0 && setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
        />
        {searching && <span style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',color:'rgba(255,255,255,0.3)',fontSize:16 }}>...</span>}
        {validated && <span style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',color:'#ED4D1D',fontSize:18 }}>✓</span>}
      </div>
      {!validated && value.length > 1 && !searching && suggestions.length === 0 && (
        <div style={{ fontSize:16,color:'#e08080',marginTop:4 }}>Scegli una città dall'elenco</div>
      )}
      {showSug && suggestions.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:999,
          background:'#1F1C18',border:'1px solid rgba(255,255,255,0.1)',
          borderRadius:12,marginTop:4,overflow:'hidden',boxShadow:'0 8px 24px rgba(0,0,0,0.6)' }}>
          {suggestions.map(c => (
            <div key={c} style={{ padding:'13px 16px',fontSize:17,cursor:'pointer',
              borderBottom:'1px solid rgba(255,255,255,0.05)',color:'#E8DDD0',
              display:'flex',alignItems:'center',gap:8 }}
              onMouseDown={() => { setValidated(true); onChange(c, true); setSuggestions([]); setShowSug(false); }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Visualizzatore documento legale ─────────────────────────────────────────
function LegalDocViewer({ title, text, onBack }) {
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ED4D1D', fontSize: 16, fontWeight: 700, padding: '0 0 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Torna
      </button>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#E8DDD0', marginBottom: 14 }}>{title}</div>
      <div style={{ maxHeight: 340, overflowY: 'auto', background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.08)' }}>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, margin: 0, fontFamily: 'inherit' }}>
          {text}
        </pre>
      </div>
    </div>
  );
}

// ─── Inserimento del codice ricevuto per email ───────────────────────────────
// Usato sia dall'accesso sia dalla registrazione: fa digitare il codice, lo
// verifica e aggancia l'eventuale profilo preesistente. Cosa succede dopo lo
// decide chi lo usa, tramite onVerified.
function CodeStep({ email, onVerified, onChangeEmail, confermaLabel = 'Entra', autoSend = true }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const sentRef = useRef(false);

  const invia = useCallback(async (silenzioso) => {
    setSending(true); setError(''); setInfo('');
    try {
      await sendLoginCode(email);
      if (!silenzioso) setInfo('Ti abbiamo mandato un nuovo codice.');
      setCooldown(60);
    } catch (e) {
      setError(messaggioErrore(e));
    } finally { setSending(false); }
  }, [email]);

  // Il codice parte da solo appena si arriva qui, una volta sola.
  useEffect(() => {
    if (!autoSend || sentRef.current) return;
    sentRef.current = true;
    invia(true);
  }, [autoSend, invia]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const conferma = async () => {
    if (code.length < 6 || loading) return;
    setLoading(true); setError(''); setInfo('');
    try {
      const session = await verifyLoginCode(email, code);
      await linkAuthUser();
      await onVerified(session);
    } catch (e) {
      setError(messaggioErrore(e));
      setLoading(false);
    }
  };

  return (
    <>
      <div style={styles.stepTitle}>Controlla l'email</div>
      <div style={styles.stepSub}>
        Abbiamo mandato un codice via email a<br />
        <strong style={{ color: '#E8DDD0' }}>{email}</strong>
      </div>

      {error && <div style={{ ...styles.error, marginTop: 16, marginBottom: 0 }}>{error}</div>}
      {info && !error && (
        <div style={{ ...styles.error, marginTop: 16, marginBottom: 0, background: 'rgba(237,77,29,0.08)', borderColor: 'rgba(237,77,29,0.25)', color: '#ED4D1D' }}>
          {info}
        </div>
      )}

      <input
        style={{ ...styles.input, marginTop: 20, fontSize: 28, letterSpacing: 8, textAlign: 'center', fontWeight: 700 }}
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
        onKeyDown={e => e.key === 'Enter' && conferma()}
        placeholder="------"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={10}
        autoFocus
      />

      <button
        style={{
          ...styles.btnPrimary, marginTop: 16,
          background: code.length >= 6 ? '#ED4D1D' : 'rgba(255,255,255,0.1)',
          color: code.length >= 6 ? '#fff' : 'rgba(255,255,255,0.3)',
          cursor: code.length >= 6 ? 'pointer' : 'default',
        }}
        disabled={code.length < 6 || loading}
        onClick={conferma}>
        {loading ? <span className="spinner" /> : confermaLabel}
      </button>

      <div style={{ marginTop: 18, textAlign: 'center', fontSize: 16, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
        Non è arrivata? Controlla anche la posta indesiderata.
        <br />
        <button
          onClick={() => invia(false)}
          disabled={sending || cooldown > 0}
          style={{
            background: 'none', border: 'none', padding: '6px 0',
            color: cooldown > 0 ? 'rgba(255,255,255,0.25)' : '#ED4D1D',
            fontSize: 16, fontWeight: 700,
            cursor: cooldown > 0 ? 'default' : 'pointer',
            fontFamily: "'Exo 2', sans-serif",
          }}>
          {sending ? 'Invio...' : cooldown > 0 ? `Rimanda tra ${cooldown}s` : 'Rimanda il codice'}
        </button>
        {onChangeEmail && (
          <>
            <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.2)' }}>·</span>
            <button
              onClick={onChangeEmail}
              style={{ background: 'none', border: 'none', padding: '6px 0', color: 'rgba(255,255,255,0.45)', fontSize: 16, cursor: 'pointer', fontFamily: "'Exo 2', sans-serif" }}>
              Cambia email
            </button>
          </>
        )}
      </div>
    </>
  );
}

// ─── Accesso ──────────────────────────────────────────────────────────────────
// Chi ha già un profilo entra. Chi verifica l'email ma non ha ancora un
// profilo viene mandato a completare la registrazione, senza rifare il codice.
function LoginFlow({ onLogin, onSwitchToRegister, onNeedsProfile }) {
  // Chi era dentro col vecchio sistema passa di qui una volta sola: gli
  // riproponiamo la sua email e gli spieghiamo perché deve rientrare.
  const [vecchioAccesso] = useState(() => {
    try {
      const raw = localStorage.getItem(LEGACY_USER_KEY);
      return raw ? (JSON.parse(raw).email || '') : '';
    } catch { return ''; }
  });

  const [fase, setFase] = useState('email');
  const [email, setEmail] = useState(vecchioAccesso);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const chiediCodice = async () => {
    const pulita = email.trim().toLowerCase();
    if (!pulita.includes('@')) { setError('Inserisci un indirizzo email valido.'); return; }
    setLoading(true); setError('');
    try {
      await sendLoginCode(pulita);
      setEmail(pulita);
      setFase('codice');
    } catch (e) {
      setError(messaggioErrore(e));
    } finally { setLoading(false); }
  };

  if (fase === 'codice') {
    return (
      <CodeStep
        email={email}
        autoSend={false}
        onChangeEmail={() => setFase('email')}
        onVerified={async () => {
          const profilo = await getMyProfile();
          localStorage.removeItem(LEGACY_USER_KEY);
          if (profilo) onLogin(profilo);
          else onNeedsProfile(email);
        }}
      />
    );
  }

  return (
    <>
      <div style={styles.stepTitle}>Ciao</div>
      <div style={styles.stepSub}>Inserisci la tua email: ti mandiamo un codice per entrare</div>

      {vecchioAccesso && !error && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'rgba(237,77,29,0.06)', border: '1px solid rgba(237,77,29,0.2)', fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
          Abbiamo aggiunto la verifica via email per proteggere i tuoi dati.
          Ti chiediamo di rientrare una volta sola: dopo resti dentro come prima.
        </div>
      )}
      {error && <div style={{ ...styles.error, marginTop: 16, marginBottom: 0 }}>{error}</div>}

      <input style={{ ...styles.input, marginTop: 20 }} type="email" placeholder="la-tua@email.it"
        value={email} onChange={e => setEmail(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && chiediCodice()}
        autoComplete="email" autoFocus />

      <button style={{ ...styles.btnPrimary, marginTop: 16 }} disabled={loading || !email.trim()} onClick={chiediCodice}>
        {loading ? <span className="spinner" /> : 'Mandami il codice'}
      </button>

      {onSwitchToRegister && (
        <button style={styles.btnLink} onClick={onSwitchToRegister}>
          Prima volta? Registrati
        </button>
      )}
    </>
  );
}

// ─── Registrazione ────────────────────────────────────────────────────────────
// I quattro passaggi di prima, più la verifica dell'email alla fine. I dati
// restano nel modulo finché la verifica non riesce: se il codice non arriva
// non si perde niente e si può correggere l'email.
const STEPS = ['Chi sei', 'Cosa suoni', 'Dove sei', 'Consensi', 'Verifica'];

function RegisterFlow({ onLogin, onSwitchToLogin, initialEmail = '', emailGiaVerificata = false, onStepChange }) {
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [mainInstrument, setMainInstrument] = useState('');
  const [sings, setSings] = useState(false);
  const [city, setCity] = useState('');
  const [cityValidated, setCityValidated] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showDoc, setShowDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Email gia' verificata: arriva cosi' dall'accesso, oppure lo diventa qui
  // sotto se il codice va a buon fine ma il profilo non c'e' ancora.
  const [verificata, setVerificata] = useState(emailGiaVerificata);
  // Chi e' gia' registrato e non se lo ricorda: appena scrive l'email lo
  // riconosciamo, e invece di altre domande gli mandiamo il codice per entrare.
  const [giaRegistrata, setGiaRegistrata] = useState(false);
  const [controllo, setControllo] = useState(false);

  // null = niente barra dei passi: per chi entra con il codice non ci sono passi.
  useEffect(() => { if (onStepChange) onStepChange(giaRegistrata ? null : step); }, [step, giaRegistrata, onStepChange]);

  const canNext0 = firstName.trim().length > 0 && lastName.trim().length > 0 && email.includes('@');
  const canFinish = termsAccepted && privacyAccepted;

  // Crea il profilo. Viene chiamata solo quando l'email è verificata.
  const creaProfilo = async (userId) => {
    // Se un profilo con questa email esiste già (per esempio un musicista
    // registrato prima dell'autenticazione), non ne creiamo un secondo.
    const esistente = await getMyProfile();
    if (esistente) { localStorage.removeItem(LEGACY_USER_KEY); onLogin(esistente); return; }

    const nuovo = await createMusician({
      first_name: firstName.trim(), last_name: lastName.trim(),
      email: email.trim().toLowerCase(),
      instruments: mainInstrument ? [mainInstrument] : [], sings,
      default_instrument: mainInstrument || null,
      default_sings: sings && !mainInstrument,
      genres: [], levels: {}, city: city.trim(),
      account_type: 'musician',
      terms_accepted_at: new Date().toISOString(),
      auth_user_id: userId,
    });
    localStorage.removeItem(LEGACY_USER_KEY);
    onLogin({ ...nuovo, accountType: 'musician' });
  };

  const vaiAllaVerifica = async () => {
    setLoading(true); setError('');
    try {
      // Se l'email è già verificata (arriviamo dall'accesso) si salta il codice.
      if (verificata) {
        const session = await getCurrentSession();
        await creaProfilo(session?.user?.id || null);
        return;
      }
      const esistente = await emailExists(email.trim().toLowerCase());
      if (esistente) {
        // Di solito lo scopriamo gia' al primo passo; qui resta come rete.
        setLoading(false);
        setGiaRegistrata(true);
        return;
      }
      setStep(4);
      setLoading(false);
    } catch {
      setError('Errore durante la registrazione. Riprova.');
      setLoading(false);
    }
  };

  // Primo "Avanti": prima di chiedere altro, l'email e' gia' di qualcuno?
  // Se il controllo non riesce (rete) si prosegue: lo ripete il passo dei
  // consensi prima di mandare il codice.
  const avantiDaChiSei = async () => {
    if (verificata) { setStep(1); return; }
    setControllo(true); setError('');
    let esiste = false;
    try { esiste = await emailExists(email.trim().toLowerCase()); }
    catch (e) { console.error(e); }
    setControllo(false);
    if (esiste) setGiaRegistrata(true);
    else setStep(1);
  };

  if (giaRegistrata) {
    return (
      <>
        <div style={{ marginBottom: 18, padding: '14px 16px', borderRadius: 12, background: 'rgba(237,77,29,0.08)',
          border: '1px solid rgba(237,77,29,0.35)', fontSize: 18, lineHeight: 1.5, color: '#E8DDD0' }}>
          <strong>Sei già dei nostri!</strong> Con questa email c'è già un account:
          non serve registrarsi di nuovo. Scrivi il codice che ti abbiamo appena mandato ed entri.
        </div>
        <CodeStep
          email={email.trim().toLowerCase()}
          confermaLabel="Entra"
          onChangeEmail={() => { setGiaRegistrata(false); setStep(0); }}
          onVerified={async () => {
            const profilo = await getMyProfile();
            localStorage.removeItem(LEGACY_USER_KEY);
            if (profilo) { onLogin(profilo); return; }
            // Account senza profilo: si completa la registrazione, senza
            // chiedere un altro codice.
            setVerificata(true); setGiaRegistrata(false); setStep(1);
          }}
        />
      </>
    );
  }

  return (
    <>
      {error && <div style={styles.error}>{error}</div>}

      {/* Step 0 — Chi sei */}
      {step === 0 && (
        <>
          <div style={styles.stepTitle}>Come ti chiami?</div>
          <div style={styles.stepSub}>Nome, cognome e email per iniziare</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <input style={styles.input} placeholder="Nome" value={firstName}
                onChange={e => setFirstName(e.target.value)} autoFocus />
              <input style={styles.input} placeholder="Cognome" value={lastName}
                onChange={e => setLastName(e.target.value)} />
            </div>
            <input style={{ ...styles.input, opacity: verificata ? 0.6 : 1 }} type="email"
              placeholder="la-tua@email.it" value={email} readOnly={verificata}
              onChange={e => setEmail(e.target.value)} autoComplete="email" />
            {verificata && <div style={{ fontSize: 16, color: '#ED4D1D' }}>Email già verificata</div>}
          </div>
          <button style={{ ...styles.btnPrimary, marginTop: 24, background: canNext0 ? '#ED4D1D' : 'rgba(255,255,255,0.1)', color: canNext0 ? '#fff' : 'rgba(255,255,255,0.3)', cursor: canNext0 ? 'pointer' : 'default' }}
            disabled={!canNext0 || controllo} onClick={avantiDaChiSei}>
            {controllo ? <span className="spinner" /> : 'Avanti →'}
          </button>
          {onSwitchToLogin && (
            <button style={styles.btnAccedi} onClick={onSwitchToLogin}>Ho già un account: accedi</button>
          )}
        </>
      )}

      {/* Step 1 — Cosa suoni */}
      {step === 1 && (
        <>
          <div style={styles.stepTitle}>Cosa suoni?</div>
          <div style={styles.stepSub}>Scegli il tuo strumento principale</div>
          <div style={{ marginTop: 20, marginBottom: 16 }}>
            <div onClick={() => { setSings(!sings); if (!sings) setMainInstrument(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 12, cursor: 'pointer', border: `2px solid ${sings ? '#ED4D1D' : 'rgba(255,255,255,0.08)'}`, background: sings ? 'rgba(237,77,29,0.08)' : 'rgba(255,255,255,0.03)', transition: 'all .15s' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: sings ? '#ED4D1D' : '#E8DDD0' }}>Voce / Canto</div>
                <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Sei un cantante</div>
              </div>
              {sings && <div style={{ marginLeft: 'auto', color: '#ED4D1D', fontSize: 22 }}>✓</div>}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {INSTRUMENTS.map(instr => {
              const selected = mainInstrument === instr;
              return (
                <div key={instr} onClick={() => setMainInstrument(selected ? '' : instr)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderRadius: 12, cursor: 'pointer', border: `2px solid ${selected ? '#ED4D1D' : 'rgba(255,255,255,0.08)'}`, background: selected ? 'rgba(237,77,29,0.08)' : 'rgba(255,255,255,0.03)', transition: 'all .15s' }}>
                  <span style={{ fontSize: 18, fontWeight: 600, color: selected ? '#ED4D1D' : '#E8DDD0' }}>{instr}</span>
                  {selected && <span style={{ color: '#ED4D1D', fontSize: 20 }}>✓</span>}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button style={styles.btnSecondary} onClick={() => setStep(0)}>← Indietro</button>
            <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={() => setStep(2)}>
              {mainInstrument || sings ? 'Avanti →' : 'Salta →'}
            </button>
          </div>
        </>
      )}

      {/* Step 2 — Dove sei */}
      {step === 2 && (
        <>
          <div style={styles.stepTitle}>Da dove suoni?</div>
          <div style={styles.stepSub}>La tua città — aiuta a trovare serate vicine</div>
          <div style={{ marginTop: 20 }}>
            <CityAutocomplete value={city} onChange={(v, valid) => { setCity(v); setCityValidated(valid); }} inputStyle={styles.input} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button style={styles.btnSecondary} onClick={() => setStep(1)}>← Indietro</button>
            <button style={{ ...styles.btnPrimary, flex: 1, ...(city.trim().length > 0 && !cityValidated ? { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)', cursor: 'default' } : {}) }}
              disabled={city.trim().length > 0 && !cityValidated} onClick={() => setStep(3)}>
              {city.trim() ? 'Avanti →' : 'Salta →'}
            </button>
          </div>
        </>
      )}

      {/* Step 3 — Consensi */}
      {step === 3 && (
        showDoc
          ? <LegalDocViewer
              title={showDoc === 'terms' ? 'Termini e Condizioni' : 'Informativa Privacy'}
              text={showDoc === 'terms' ? TERMS_CONDITIONS : PRIVACY_POLICY}
              onBack={() => setShowDoc(null)} />
          : <>
              <div style={styles.stepTitle}>Quasi fatto</div>
              <div style={styles.stepSub}>Leggi e accetta per completare la registrazione</div>
              <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { key: 'terms', label: 'Termini e Condizioni', checked: termsAccepted, toggle: () => setTermsAccepted(v => !v) },
                  { key: 'privacy', label: 'Informativa Privacy (GDPR)', checked: privacyAccepted, toggle: () => setPrivacyAccepted(v => !v) },
                ].map(({ key, label, checked, toggle }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, background: checked ? 'rgba(237,77,29,0.06)' : 'rgba(255,255,255,0.03)', border: `1.5px solid ${checked ? '#ED4D1D' : 'rgba(255,255,255,0.08)'}`, transition: 'all .15s' }}>
                    <div onClick={toggle} style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${checked ? '#ED4D1D' : 'rgba(255,255,255,0.25)'}`, background: checked ? '#ED4D1D' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all .15s' }}>
                      {checked && <span style={{ color: '#fff', fontSize: 16, fontWeight: 900 }}>✓</span>}
                    </div>
                    <span style={{ flex: 1, fontSize: 17, color: '#E8DDD0', fontWeight: 600 }}>Ho letto e accetto i/le {label}</span>
                    <button onClick={() => setShowDoc(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ED4D1D', fontSize: 16, fontWeight: 700, padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>Leggi</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button style={styles.btnSecondary} onClick={() => setStep(2)}>← Indietro</button>
                <button style={{ ...styles.btnPrimary, flex: 1, background: canFinish ? '#ED4D1D' : 'rgba(255,255,255,0.1)', color: canFinish ? '#fff' : 'rgba(255,255,255,0.3)', cursor: canFinish ? 'pointer' : 'default' }}
                  disabled={!canFinish || loading} onClick={vaiAllaVerifica}>
                  {loading ? <span className="spinner" /> : verificata ? 'Registrati' : 'Verifica email →'}
                </button>
              </div>
            </>
      )}

      {/* Step 4 — Verifica email */}
      {step === 4 && (
        <CodeStep
          email={email.trim().toLowerCase()}
          confermaLabel="Completa la registrazione"
          onChangeEmail={() => setStep(0)}
          onVerified={async (session) => { await creaProfilo(session?.user?.id || null); }}
        />
      )}
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  screen: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateRows: '1fr auto 1fr',
    justifyItems: 'center',
    padding: 'max(env(safe-area-inset-top),32px) 20px max(env(safe-area-inset-bottom),32px)',
    background: '#000000',
    overflowY: 'auto',
  },
  logoSpace: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  card: {
    width: '100%', maxWidth: 440,
    background: '#1F1C18',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 20,
    padding: '28px 24px',
  },
  stepTitle: { fontSize: 26, fontWeight: 800, color: '#E8DDD0', marginBottom: 6, lineHeight: 1.2 },
  stepSub: { fontSize: 17, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.05)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: 12, padding: '14px 16px',
    color: '#E8DDD0', fontSize: 18,
    fontFamily: "'Exo 2', sans-serif",
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .15s',
  },
  btnPrimary: {
    width: '100%', padding: '15px', borderRadius: 12, border: 'none',
    background: '#ED4D1D', color: '#fff', fontSize: 18, fontWeight: 800,
    cursor: 'pointer', transition: 'all .15s', display: 'block', textAlign: 'center',
    fontFamily: "'Exo 2', sans-serif", minHeight: 44,
  },
  btnSecondary: {
    padding: '14px 20px', borderRadius: 12,
    background: 'rgba(255,255,255,0.06)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    color: '#B5AA9A', fontSize: 17, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Exo 2', sans-serif",
    whiteSpace: 'nowrap', minHeight: 44,
  },
  // "Ho gia' un account": grande quanto il pulsante principale, con il bordo
  // arancione invece del pieno. Prima era una riga grigia che non si notava, e
  // chi tornava per una seconda serata rifaceva la registrazione.
  btnAccedi: {
    width: '100%', marginTop: 12, padding: '14px', borderRadius: 12,
    background: 'transparent', border: '2px solid #ED4D1D',
    color: '#fff', fontSize: 18, fontWeight: 800,
    cursor: 'pointer', display: 'block', textAlign: 'center',
    fontFamily: "'Exo 2', sans-serif", minHeight: 54,
  },
  btnLink: {
    display: 'block', width: '100%', textAlign: 'center',
    marginTop: 16, padding: '8px',
    background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.35)', fontSize: 16,
    cursor: 'pointer', fontFamily: "'Exo 2', sans-serif", minHeight: 44,
  },
  error: {
    background: 'rgba(160,80,80,0.12)', border: '1px solid rgba(160,80,80,0.25)',
    borderRadius: 10, padding: '12px 14px', marginBottom: 16,
    fontSize: 16, color: '#e08080',
  },
};

// ─── Barra di avanzamento della registrazione ────────────────────────────────
function ProgressBar({ step }) {
  if (step == null) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
        <div style={{ height: 3, background: '#ED4D1D', borderRadius: 2, width: `${((step + 1) / STEPS.length) * 100}%`, transition: 'width .3s' }} />
      </div>
      {/* Solo il passo in corso: i cinque nomi affiancati a 16px non ci stanno
          in un telefono, e si accavallavano ("ConsensiVerifica"). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
        <span style={{ fontSize: 16, color: '#ED4D1D', fontWeight: 700 }}>{STEPS[step]}</span>
        <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', fontWeight: 600, whiteSpace: 'nowrap' }}>Passo {step + 1} di {STEPS.length}</span>
      </div>
    </div>
  );
}

// ─── Schermata intera ─────────────────────────────────────────────────────────
// verifiedEmail: c'e' gia' una sessione verificata ma nessun profilo — succede
// a chi abbandona la registrazione a meta'. Si riparte da dove era rimasto,
// senza chiedergli un altro codice.
function AuthScreen({ onLogin, initialMode = 'login', verifiedEmail = '' }) {
  const [mode, setMode] = useState(verifiedEmail ? 'register' : initialMode);
  const [emailVerificata, setEmailVerificata] = useState(verifiedEmail);
  const [registerStep, setRegisterStep] = useState(0);

  return (
    <div style={styles.screen}>
      <div style={styles.logoSpace}>
        <img src="/logo.png" alt="Jammo" style={{ height: mode === 'login' ? 160 : 128, objectFit: 'contain' }} />
      </div>

      <div style={{ width: '100%', maxWidth: 440 }}>
        {mode === 'register' && <ProgressBar step={registerStep} />}
        <div style={styles.card}>
          {mode === 'login'
            ? <LoginFlow
                onLogin={onLogin}
                onSwitchToRegister={() => setMode('register')}
                onNeedsProfile={(email) => { setEmailVerificata(email); setMode('register'); }} />
            : <RegisterFlow
                onLogin={onLogin}
                onSwitchToLogin={emailVerificata ? null : () => setMode('login')}
                initialEmail={emailVerificata}
                emailGiaVerificata={!!emailVerificata}
                onStepChange={setRegisterStep} />
          }
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <span style={{ fontSize: 16, color: '#f5c842', fontWeight: 600 }}>Servizio non attivo — in fase di test</span>
      </div>
    </div>
  );
}

export default AuthScreen;

// ─── Versioni in finestra, usate da EventPreview ─────────────────────────────
export function LoginModal({ onLogin, onSwitchToRegister, onClose }) {
  // onSwitchToRegister riceve l'email quando questa e' gia' stata verificata,
  // cosi' la registrazione non ne chiede un'altra volta il codice.
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/logo.png" alt="Jammo" style={{ height: 104, objectFit: 'contain' }} />
      </div>
      <LoginFlow
        onLogin={onLogin}
        onSwitchToRegister={onSwitchToRegister}
        onNeedsProfile={(email) => onSwitchToRegister(email)} />
    </Modal>
  );
}

export function RegisterStepsModal({ onLogin, onSwitchToLogin, onClose, verifiedEmail = '' }) {
  const [step, setStep] = useState(0);
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img src="/logo.png" alt="Jammo" style={{ height: 104, objectFit: 'contain' }} />
      </div>
      <ProgressBar step={step} />
      <RegisterFlow
        onLogin={onLogin}
        onSwitchToLogin={verifiedEmail ? null : onSwitchToLogin}
        initialEmail={verifiedEmail}
        emailGiaVerificata={!!verifiedEmail}
        onStepChange={setStep} />
    </Modal>
  );
}
